import { AppLayout } from "@bcc/components/app-layout";
import { exportBatchesToCsv } from "@bcc/components/batches/batches-export";
import { CommonHeader } from "@bcc/components/common-header";
import { ShippingDocumentsDialog } from "@bcc/components/orders/shipping-documents-dialog/shipping-documents-dialog";
import { DemoDataTag } from "@bcc/components/tour/demo-data/demo-data-tag";
import { useDemoBatches } from "@bcc/components/tour/demo-data/use-demo-batches";
import { TOUR_IDS } from "@bcc/components/tour/tour-ids";
import { Button } from "@bcc/components/ui/button";
import { Input } from "@bcc/components/ui/input";
import { BatchesView } from "@bcc/components/views/batches-view";
import { getBatchStatusFilterValue, getVisibleBatches, type BatchStatusFilterValue } from "@bcc/components/views/batches-view.utils";
import { useBatchesContext } from "@bcc/contexts/batches-context";
import { getShippingDocumentLoadingKey } from "@bcc/contexts/batches-context.utils";
import { useToast } from "@bcc/hooks/use-toast";
import { useVendorContext } from "@client-shared/providers/vendor";
import type { LabelPrintBatch, LabelPrintBatchDetail, PurchaseOrder, ShippingDocumentsTabType } from "@shared/types";
import { Download, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setBatchesPageExportButtonTestId } from "./batches-page.utils";

interface ShippingDialogState {
  open: boolean;
  initialTab: ShippingDocumentsTabType;
  purchaseOrders: PurchaseOrder[];
  manifestPdfUrl?: string;
}

const noop = (): void => undefined;

/** Stable empty list for the one tick while the sample batches load */
const NO_BATCHES: LabelPrintBatch[] = [];

const CLOSED_SHIPPING_DIALOG: ShippingDialogState = {
  open: false,
  initialTab: "combined",
  purchaseOrders: [],
};

function BatchesPageContent() {
  const [batchesState, , batchesUtils] = useBatchesContext();
  const [{ customerAccountCarrierDetails }] = useVendorContext();
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shippingDialog, setShippingDialog] = useState<ShippingDialogState>(CLOSED_SHIPPING_DIALOG);
  const manifestRequestIdRef = useRef(0);
  const selectedBatchIdRef = useRef<string | null>(null);

  const batchId = searchParams.get("batchId");
  const batchSearchValue = searchParams.get("batchSearch") ?? "";
  const batchStatusFilter = getBatchStatusFilterValue(searchParams.get("batchStatus"));
  const selectedBatchId = batchId ?? null;
  const isGeneratingManifest = Boolean(
    selectedBatchId && batchesState.loadingKeys[getShippingDocumentLoadingKey(selectedBatchId, "Manifest")]
  );

  useEffect(() => {
    selectedBatchIdRef.current = selectedBatchId;
  }, [selectedBatchId]);

  // The manifest is a server-generated PDF behind a short-lived pre-signed URL.
  // The Manifest button shows a spinner (via the context loading key) while the
  // URL is fetched or the manifest is created; the preview dialog opens once
  // the URL is known so it can fit the PDF to the selected paper size. A slow
  // response may only open the dialog if it is still the latest document
  // request and the user is still on the batch it belongs to; otherwise it
  // would replace a newer preview or show the wrong batch's manifest.
  const handleGenerateManifest = useCallback(
    async (batch: LabelPrintBatchDetail) => {
      const requestId = ++manifestRequestIdRef.current;
      const url = await batchesUtils.getLabelPrintBatchShippingDocumentUrl(batch, "Manifest");
      if (requestId !== manifestRequestIdRef.current || batch.batchId !== selectedBatchIdRef.current) {
        return;
      }
      if (!url) {
        toast({ title: t("views.batches.toasts.manifestUnavailable"), variant: "destructive" });
        return;
      }
      setShippingDialog({
        open: true,
        initialTab: "manifest",
        purchaseOrders: batch.purchaseOrders ?? [],
        manifestPdfUrl: url,
      });
    },
    [batchesUtils, t, toast]
  );

  // Slips + combined docs are generated client-side (pdf-lib) via the shared
  // ShippingDocumentsDialog rather than routed through S3, because the batch
  // pipeline only produces labels + manifest today — it doesn't yet write a
  // packingSlipsObjectKey or labelsWithPackingSlipsObjectKey. This matches the
  // client app's batch flow; revisit once the backend writes those object keys.
  // Opening any other preview also supersedes a manifest request still in
  // flight, so its late response cannot replace the preview the user chose.
  const openShippingDialog = useCallback((batch: LabelPrintBatchDetail, initialTab: ShippingDocumentsTabType) => {
    manifestRequestIdRef.current += 1;
    setShippingDialog({
      open: true,
      initialTab,
      purchaseOrders: batch.purchaseOrders ?? [],
    });
  }, []);

  const handleReprintLabels = useCallback(
    (batch: LabelPrintBatchDetail) => openShippingDialog(batch, "shipping-label"),
    [openShippingDialog]
  );

  const handleReprintPackingSlips = useCallback(
    (batch: LabelPrintBatchDetail) => openShippingDialog(batch, "packing-slip"),
    [openShippingDialog]
  );

  const handleReprintLabelsAndPackingSlips = useCallback(
    (batch: LabelPrintBatchDetail) => openShippingDialog(batch, "combined"),
    [openShippingDialog]
  );

  const handleShippingDialogOpenChange = useCallback((open: boolean) => {
    setShippingDialog((prev) => (open ? { ...prev, open: true } : CLOSED_SHIPPING_DIALOG));
  }, []);

  const handleRetryBatch = useCallback(
    async (request: Parameters<typeof batchesUtils.createBatch>[0]) => {
      await batchesUtils.createBatch(request);
    },
    [batchesUtils]
  );

  const handleOpenSummary = useCallback(
    (batch: LabelPrintBatchDetail) => {
      navigate(`/batches/${batch.batchId}/summary`);
    },
    [navigate]
  );

  const handleBatchSelect = (nextBatchId?: string) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (nextBatchId) {
      nextSearchParams.set("batchId", nextBatchId);
    } else {
      nextSearchParams.delete("batchId");
    }

    setSearchParams(nextSearchParams);
  };

  // Sample batches replace the roaster's while the tour runs
  const demoBatches = useDemoBatches();
  const batchesSource = demoBatches
    ? {
        batches: demoBatches.batches ?? NO_BATCHES,
        selectedBatch: demoBatches.batch ?? null,
        isLoading: demoBatches.isLoading,
        // Selecting via the URL would fetch the sample id from the server, so it is pinned
        selectedBatchId: demoBatches.batch?.batchId ?? null,
        onSelectBatch: noop,
      }
    : {
        batches: batchesState.batches,
        selectedBatch: batchesState.batch ?? null,
        isLoading: batchesState.isLoading,
        selectedBatchId,
        onSelectBatch: handleBatchSelect,
      };

  const visibleBatches = useMemo(
    () =>
      getVisibleBatches({
        batches: batchesSource.batches,
        searchValue: batchSearchValue,
        statusFilter: batchStatusFilter,
      }),
    [batchesSource.batches, batchSearchValue, batchStatusFilter]
  );

  const handleExport = useCallback(() => {
    // Sample rows must never be written to a file
    if (demoBatches) return;
    exportBatchesToCsv(visibleBatches, t);
  }, [demoBatches, visibleBatches, t]);

  const isExportDisabled = batchesSource.isLoading || visibleBatches.length === 0;

  const handleSearchChange = (value: string) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (value.trim()) {
      nextSearchParams.set("batchSearch", value);
    } else {
      nextSearchParams.delete("batchSearch");
    }

    setSearchParams(nextSearchParams);
  };

  const handleBatchStatusFilterChange = (nextStatusFilter: BatchStatusFilterValue) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (nextStatusFilter === "all") {
      nextSearchParams.delete("batchStatus");
    } else {
      nextSearchParams.set("batchStatus", nextStatusFilter);
    }

    setSearchParams(nextSearchParams);
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        <CommonHeader
          title={t("views.batches.title")}
          description={t("views.batches.description")}
          actions={
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={isExportDisabled}
              data-tour-id={TOUR_IDS.batches.export}
              testId={setBatchesPageExportButtonTestId()}
            >
              <Download className="mr-2 h-4 w-4" />
              {t("views.batches.export")}
            </Button>
          }
        >
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md" data-tour-id={TOUR_IDS.batches.search}>
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                placeholder={t("views.batches.searchPlaceholder")}
                value={batchSearchValue}
                onChange={(event) => handleSearchChange(event.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CommonHeader>

        <DemoDataTag />
        <BatchesView
          batchesData={batchesSource.batches}
          batch={batchesSource.selectedBatch}
          customerAccountCarrierDetails={customerAccountCarrierDetails}
          isLoading={batchesSource.isLoading}
          isRetrying={Boolean(batchesState.loadingKeys.createBatch)}
          isGeneratingManifest={isGeneratingManifest}
          searchValue={batchSearchValue}
          statusFilter={batchStatusFilter}
          selectedBatchId={batchesSource.selectedBatchId}
          onSelectBatch={batchesSource.onSelectBatch}
          onStatusFilterChange={handleBatchStatusFilterChange}
          onGenerateManifest={handleGenerateManifest}
          onReprintLabels={handleReprintLabels}
          onReprintPackingSlips={handleReprintPackingSlips}
          onReprintLabelsAndPackingSlips={handleReprintLabelsAndPackingSlips}
          onOpenSummary={handleOpenSummary}
          onRetryBatch={handleRetryBatch}
        />
      </div>
      {shippingDialog.open && (
        <ShippingDocumentsDialog
          open={shippingDialog.open}
          onOpenChange={handleShippingDialogOpenChange}
          purchaseOrders={shippingDialog.purchaseOrders}
          manifestPdfUrl={shippingDialog.manifestPdfUrl}
          initialTab={shippingDialog.initialTab}
        />
      )}
    </AppLayout>
  );
}

export function BatchesPage() {
  return <BatchesPageContent />;
}
